import { destinationKey } from "./travellerRoutePolicy.mjs";
import { equal, mergeItems, mergeValue } from "./recordMerge.mjs";

/**
 * Arrays merged per entry, with what identifies an entry — and whether that
 * identity is a stored `id` or one lent for the merge and taken back after. A
 * destination has no id of its own; a traveller's is real data and has to
 * survive the round trip.
 */
const IDENTIFIED = {
  // A visit, not a place: trips return to the same city, and two Paris visits
  // keyed only by name would collide and turn every destination save into a
  // conflict. Where plus when is what makes a visit itself.
  // Kept, not lent: the merged list carries every destination's id, so the
  // first destination edit on an older trip stores ids for all of them.
  destinations: { key: (item: Body) => destinationKey(item), lent: false },
  travellers: { key: (item: Body) => String(item.id ?? ''), lent: false },
  accommodations: { key: (item: Body) => String(item.id ?? ''), lent: false },
} as const;

type Body = Record<string, unknown>;

/**
 * Never written through this path, whatever the editor sends. `createdAt` is a
 * server timestamp the rules require on the document and is set once, on
 * create; `id` is the document's own name; `ownerId` decides who may change
 * membership. The rules reject all three anyway — leaving them out means a save
 * that merely omits one can't ask.
 */
const STRUCTURAL = new Set(['createdAt', 'id', 'ownerId', 'participation']);

/**
 * The fields this save should actually write, merged against what is stored.
 * Returns an empty object when the caller changed nothing.
 */
export function mergeTripConfig(latest: Body, before: Body, after: Body): Body {
  const changes: Body = {};
  // Only keys the caller SENT. A key missing from `after` is not a request to
  // clear it — it means the caller's copy never had it, which is exactly what a
  // form holding a snapshot from before someone was invited looks like. The old
  // key-level merge left absent keys alone for that reason (it was the fix for
  // invites vanishing), and writing them as null here would bring that back.
  // Clearing a field means sending it: '' or [] or null, explicitly.
  for (const key of Object.keys(after)) {
    if (STRUCTURAL.has(key)) continue;
    if (equal(before[key], after[key])) continue; // Untouched by this editor.
    if (key in IDENTIFIED) {
      const { key: identify, lent } = IDENTIFIED[key as keyof typeof IDENTIFIED];
      const lists = [latest[key], before[key], after[key]].map(list => (list ?? []) as Body[]);
      // mergeItems works by `id`, so a destination is lent one for the merge and
      // has it taken back after. An entry with no identity to give (a legacy
      // accommodation saved before ids) can't be merged that way at all, so
      // mergeItems gets the raw arrays and its own ID-less path takes the whole
      // value or rejects.
      const tagged = lists.map(list => list.map(item => ({ ...item, id: identify(item) })));
      // Keyable only if every entry has an identity AND no two share one. Two
      // destinations with the same name arriving the same day would otherwise
      // collide, and mergeItems refuses duplicate ids with "changed on another
      // device" — a baffling error for someone who is the only one editing.
      // Colliding lists take the whole-value path instead, which only
      // conflicts when two people really changed the list at once.
      const keyable = tagged.every(list =>
        list.every(item => item.id) && new Set(list.map(item => item.id)).size === list.length);
      const merged = keyable ? mergeItems(tagged[0], tagged[1], tagged[2]) : mergeItems(lists[0], lists[1], lists[2]);
      changes[key] = keyable && lent ? merged.map(({ id: _id, ...rest }) => rest) : merged;
    } else {
      const value = mergeValue(latest[key], before[key], after[key]);
      changes[key] = value === undefined ? null : value;
    }
  }
  return changes;
}
